import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Send, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
];

function fmtPct(pct) {
  if (pct === null || pct === undefined) return "new";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${Math.round(pct)}%`;
}

function pctToneClass(pct) {
  if (pct === null || pct === undefined) return "text-blue-300 bg-blue-500/10 border-blue-500/30";
  if (pct >= 25) return "text-emerald-300 bg-emerald-500/10 border-emerald-500/30";
  if (pct <= -25) return "text-red-300 bg-red-500/10 border-red-500/30";
  return "text-gray-300 bg-white/5 border-white/10";
}

/**
 * Iter197 — Growth Alerts Digest widget for the Manager Dashboard.
 *
 * Reads `/api/analytics/growth-digest?days=N` and renders top sources
 * ranked by cash collected with a delta pill vs the prior N days.
 * Also shows a "Cooling off" section for sources that dropped to zero
 * this window. "Send to Discord" fires the same embed the weekly cron
 * would — useful for demo + preview.
 */
export function GrowthDigestWidget() {
  const [days, setDays] = useState(7);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async (nextDays) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/analytics/growth-digest?days=${nextDays}&limit=5`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDigest(d);
    } catch (e) {
      console.error("growth-digest load", e);
      setDigest(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const sendToDiscord = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API}/analytics/growth-digest/preview-send`, {
        method: "POST", credentials: "include",
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(body?.detail || "Send failed.");
        return;
      }
      if (body?.sent) {
        toast.success(`Growth digest sent to Discord (${body.sources || 0} sources)`);
      } else {
        toast.info(
          body?.reason === "empty"
            ? "Nothing to send yet — no deal activity in the window."
            : body?.reason === "no_discord"
              ? "Configure Discord to receive digests."
              : "Nothing sent.",
        );
      }
    } catch {
      toast.error("Network error.");
    } finally {
      setSending(false);
    }
  };

  const sources = digest?.sources || [];
  const cooled = digest?.cooled_sources || [];
  const totals = digest?.totals?.current || { deal_count: 0, cash_collected: 0 };
  const overallPct = digest?.totals?.delta_pct;

  return (
    <div
      className="bg-[#111114] border border-white/10 rounded-2xl p-4 sm:p-6 space-y-4"
      data-testid="growth-digest-widget"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <TrendingUp className="w-4 h-4 text-amber-400" />
        <h3 className="text-white font-semibold text-base sm:text-lg">Growth Alerts</h3>
        <span className="text-xs text-gray-500">by deal source</span>
        <div className="ml-auto flex items-center gap-2">
          <Select
            value={String(days)}
            onValueChange={(v) => setDays(parseInt(v, 10))}
          >
            <SelectTrigger
              data-testid="growth-digest-period"
              className="h-7 w-[110px] bg-white/5 border-white/10 text-white text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              {PERIOD_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)} className="text-white text-xs">
                  Last {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm" variant="ghost"
            onClick={() => load(days)}
            disabled={loading}
            data-testid="growth-digest-refresh"
            className="h-7 px-2 text-gray-300 hover:text-white hover:bg-white/10"
            title="Refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={sendToDiscord}
            disabled={sending || sources.length === 0}
            data-testid="growth-digest-send"
            className="h-7 px-2 text-gray-300 hover:text-white hover:bg-white/10"
            title="Send this digest to Discord now"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            {!sending && <span className="text-[11px]">Send</span>}
          </Button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="flex flex-wrap items-baseline gap-3 pb-2 border-b border-white/5">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Total cash</div>
          <div className="text-lg font-semibold text-white" data-testid="growth-digest-total-cash">
            ${totals.cash_collected.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Deals</div>
          <div className="text-lg font-semibold text-white">{totals.deal_count.toLocaleString()}</div>
        </div>
        {overallPct !== null && overallPct !== undefined && (
          <div className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${pctToneClass(overallPct)}`}>
            {overallPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {fmtPct(overallPct)} vs prior
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="growth-digest-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-white/5 border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : sources.length === 0 && cooled.length === 0 ? (
        <div
          className="text-center py-8 px-4 border border-dashed border-white/10 rounded-xl"
          data-testid="growth-digest-empty"
        >
          <TrendingUp className="w-6 h-6 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No deals in this window yet.</p>
          <p className="text-xs text-gray-500 mt-1">
            Close a deal or pipe one in from Whop / Stripe / Close CRM to seed the digest.
          </p>
        </div>
      ) : (
        <>
          {sources.length > 0 && (
            <div className="space-y-2" data-testid="growth-digest-sources">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Top movers</div>
              {sources.map((s) => (
                <div
                  key={s.source}
                  data-testid={`growth-digest-row-${s.source}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-white font-medium capitalize truncate">
                      {s.source}
                    </span>
                    <span className="text-[11px] text-gray-500 shrink-0">
                      {s.deal_count} deals
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm text-white font-mono">
                      ${s.cash_collected.toLocaleString()}
                    </span>
                    <span
                      className={`inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded-full border ${pctToneClass(s.pct_change)}`}
                    >
                      {s.pct_change !== null && s.pct_change !== undefined && (
                        s.pct_change >= 0
                          ? <TrendingUp className="w-2.5 h-2.5" />
                          : <TrendingDown className="w-2.5 h-2.5" />
                      )}
                      {fmtPct(s.pct_change)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {cooled.length > 0 && (
            <div className="space-y-2 pt-2" data-testid="growth-digest-cooled">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Cooling off</div>
              {cooled.map((s) => (
                <div
                  key={s.source}
                  className="flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-gray-400 capitalize truncate">{s.source}</span>
                    <span className="text-[11px] text-gray-600 shrink-0">
                      was ${s.prior_cash_collected.toLocaleString()} · {s.prior_deal_count} deals
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded-full border text-red-300 bg-red-500/10 border-red-500/30 shrink-0">
                    <TrendingDown className="w-2.5 h-2.5" />
                    -100%
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
